import { useMemo, useState, useCallback, useEffect, type DragEvent, type RefObject } from 'react';
import { useFieldBuilder } from '../hooks/useFieldBuilder';
import { findUnmappedVariables } from '../converters/templateVariableDetector';
import FieldCard from './FieldCard';
import JsonEditor from '../../../components/jsonEditor/JsonEditor';
import InfoLabel from '../../../ui/InfoLabel/InfoLabel';
import { Plus, Sparkles, ListPlus, FileJson, ChevronUp, ChevronDown,  } from 'lucide-react';
import styles from './InputFieldsStep.module.css';
import ToggleSwitch from '../../../ui/ToggleSwitch/ToggleSwitch';
import { useConnectionsStore } from '../../../store/connectionsStore';

interface InputFieldsStepProps {
  watch: any;
  setValue: any;
  register: any;
  errors: any;
  canEdit: boolean;
  clearErrors: any;
  syncSchemaRef?: RefObject<((schema: string) => void) | null>;
}

export default function InputFieldsStep({
  watch,
  setValue,
  canEdit,
  clearErrors,
  syncSchemaRef,
}: InputFieldsStepProps) {
  const { fields, addField, updateField, removeField, moveField, setFields, syncFromSchema } =
    useFieldBuilder({ watch, setValue });

  const connectors = useConnectionsStore(s => s.connectors);
  const isMssqlConnector = connectors.find(c => c.id === watch('mainConnectionId'))?.connectionTypeId === 7;

  useEffect(() => {
    if (syncSchemaRef) syncSchemaRef.current = syncFromSchema;
  }, [syncFromSchema, syncSchemaRef]);

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showSchemaJson, setShowSchemaJson] = useState(false);
  // const [expandSignal, setExpandSignal] = useState<{ expand: boolean; v: number } | undefined>(undefined);
  // const [allExpanded, setAllExpanded] = useState(false);

  const httpMethod = (watch('httpMethod') as string) || 'POST';
  const isGet = httpMethod === 'GET';
  const bodyTemplate = (watch('bodyTemplate') as string) || '';
  const path = (watch('path') as string) || '';
  const queryMap = (watch('queryMap') as Array<{ key: string; value: string; dynamic: boolean }>) || [];
  const fieldNames = fields.map(f => f.name);

  /** Collect all {{var}} references from URL path + dynamic query params */
  const urlVariables = useMemo(() => {
    const vars = new Set<string>();
    const extractFieldName = (expression: string) =>
      expression
        .split('|')[0]
        .trim()
        .match(/^[A-Za-z_][A-Za-z0-9_]*/)?.[0];

    // Path params: {{param}} in URL path (before query string)
    const pathOnly = path.split('?')[0];
    for (const m of pathOnly.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) {
      const fieldName = extractFieldName(m[1]);
      if (fieldName) {
        vars.add(fieldName);
      }
    }
    // Dynamic query params: {{param}} in value
    for (const q of queryMap) {
      for (const m of (q.value || '').matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)) {
        const fieldName = extractFieldName(m[1]);
        if (fieldName) {
          vars.add(fieldName);
        }
      }
    }
    return Array.from(vars);
  }, [path, queryMap]);

  const unmappedVars = useMemo(() => {
    const fieldSet = new Set(fieldNames);
    if (isGet) {
      // GET: only URL path + query params
      return urlVariables.filter(v => !fieldSet.has(v));
    }
    // POST: URL params + body template params
    const bodyVars = findUnmappedVariables(bodyTemplate, []);
    const allVars = [...new Set([...urlVariables, ...bodyVars])];
    return allVars.filter(v => !fieldSet.has(v));
  }, [isGet, urlVariables, bodyTemplate, fieldNames]);

  const handleAutoCreate = () => {
    const newFields = unmappedVars.map(varName => ({
      id: crypto.randomUUID(),
      name: varName,
      label: varName
        .replace(/[_-]+/g, ' ')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim(),
      type: 'string' as const,
      description: '',
      required: true,
      example: '',
      nameIsCustom: true,
    }));
    setFields([...fields, ...newFields]);
  };

  const handleDragStart = useCallback((idx: number) => (e: DragEvent<HTMLDivElement>) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  }, []);

  const handleDragOver = useCallback((idx: number) => (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(idx);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      moveField(dragIndex, dragOverIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  }, [dragIndex, dragOverIndex, moveField]);

  return (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <h3 className={styles.stepTitle}>Tool Input Fields</h3>
        <p className={styles.stepDesc}>
          Define the parameters this tool expects from the user. Each field becomes part of the MCP input schema - the AI will ask the user for these values when the tool is called.
        </p>
      </div>
      <div className={styles.fieldForm}>
        <ToggleSwitch
          checked={watch('formEnabled')}
          onChange={(checked) => setValue('formEnabled', checked, { shouldDirty: true })}
        />
        <label className={styles.interactiveForm}>Interactive Forms - define if values are collected via forms</label>
      </div>
      <div className={styles.advancedHeader}>
        <button
          type="button"
          className={styles.collapseBtn}
          onClick={() => setShowSchemaJson(!showSchemaJson)}
        >
          {showSchemaJson ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          <FileJson size={14} />
          MCP Input Schema JSON
        </button>
        <InfoLabel content="This is the JSON schema sent to MCP. Changes here will update the visual fields below." />
      </div>
      {showSchemaJson && (
        <div className={styles.schemaJsonSection}>

          <JsonEditor
            value={watch('inputSchema')}
            onChange={(value) => {
              clearErrors('inputSchema');
              syncFromSchema(value);
            }}
            height="300px"
            title="MCP Input Schema"
            readOnly={!canEdit}
          />
        </div>
      )}

      {unmappedVars.length > 0 && (
        <div className={styles.detectBanner}>
          <div className={styles.detectIcon}>
            <Sparkles size={18} />
          </div>
          <div className={styles.detectContent}>
            <strong>
              Found {unmappedVars.length} field{unmappedVars.length > 1 ? 's' : ''} in your {isGet ? 'URL parameters' : 'request template'}
            </strong>
            <span className={styles.detectVars}>
              {unmappedVars.join(', ')}
            </span>
          </div>
          <button
            type="button"
            className={styles.detectBtn}
            onClick={handleAutoCreate}
          >
            <ListPlus size={14} />
            Create Automatically
          </button>
        </div>
      )}

      {/* {fields.length > 0 && (
        <div className={styles.fieldListHeader}>
          <button
            type="button"
            className={styles.collapseBtn}
            onClick={() => {
              const next = !allExpanded;
              setAllExpanded(next);
              setExpandSignal(prev => ({ expand: next, v: (prev?.v ?? 0) + 1 }));
            }}
          >
            {allExpanded ? <ChevronsDownUp size={14} /> :
              <ChevronsUpDown size={14} style={{ transform: 'rotate(180deg)' }} />}
          </button>
        </div>
      )} */}

      <div className={styles.fieldList}>
        {fields.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <ListPlus size={32} />
            </div>
            <h3 className={styles.emptyTitle}>No fields yet</h3>
            <p className={styles.emptyDescription}>
              Add the information this tool needs to run. Each field becomes a parameter the AI will collect from the user.
            </p>
            {canEdit && (
              <button
                type="button"
                className={styles.addBtnLarge}
                onClick={addField}
              >
                <Plus size={16} />
                Add First Field
              </button>
            )}
          </div>
        ) : (
          <>
            {fields.map((field, idx) => (
              <FieldCard
                key={field.id}
                field={field}
                index={idx}
                canEdit={canEdit}
                isMssqlConnector={isMssqlConnector}
                onChange={(updates) => updateField(field.id, updates)}
                onRemove={() => removeField(field.id)}
                onDragStart={handleDragStart(idx)}
                onDragOver={handleDragOver(idx)}
                onDragEnd={handleDragEnd}
                isDragOver={dragOverIndex === idx && dragIndex !== idx}
                // expandSignal={expandSignal}
              />
            ))}
            {canEdit && (
              <button
                type="button"
                className={styles.addBtn}
                onClick={addField}
              >
                <Plus size={14} />
                Add Field
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
